import { AttributesSelector, Box, Input, Text } from '@workwhile/ui'
import React, { useEffect, useMemo, useState } from 'react'
import type { HubSelectOption } from '../../context/i9HubSelectOptions'
import { HubUrlSelectFilter } from '../HubUrlSelectFilter'

export type I9HubWorkerUrlFilterValues = {
  state: string | null
  market: string | null
  workerId: number | null
  workerSearch: string
}

type I9HubWorkerUrlFiltersProps = {
  stateSelectOptions: HubSelectOption[]
  marketSelectOptions: HubSelectOption[]
  filters: I9HubWorkerUrlFilterValues
  onFiltersChange: (patch: Partial<I9HubWorkerUrlFilterValues>) => void
}

export function I9HubWorkerUrlFilters({
  stateSelectOptions,
  marketSelectOptions,
  filters,
  onFiltersChange,
}: I9HubWorkerUrlFiltersProps) {
  const [workerIdInput, setWorkerIdInput] = useState(
    filters.workerId != null ? String(filters.workerId) : ''
  )
  const [workerSearchInput, setWorkerSearchInput] = useState(
    filters.workerSearch
  )

  useEffect(() => {
    setWorkerIdInput(filters.workerId != null ? String(filters.workerId) : '')
  }, [filters.workerId])

  useEffect(() => {
    setWorkerSearchInput(filters.workerSearch)
  }, [filters.workerSearch])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (workerSearchInput !== filters.workerSearch) {
        onFiltersChange({ workerSearch: workerSearchInput })
      }
    }, 300)
    return () => window.clearTimeout(timeout)
  }, [workerSearchInput, filters.workerSearch, onFiltersChange])

  const workerIdSelectedAttributes = useMemo(() => {
    if (filters.workerId == null) return []
    return [String(filters.workerId)]
  }, [filters.workerId])

  const workerNameSelectedAttributes = useMemo(() => {
    const searchQuery = filters.workerSearch.trim()
    return searchQuery ? [searchQuery] : []
  }, [filters.workerSearch])

  const applyWorkerId = () => {
    const trimmed = workerIdInput.trim()
    const parsed = trimmed ? Number.parseInt(trimmed, 10) : null
    onFiltersChange({
      workerId: parsed != null && !Number.isNaN(parsed) ? parsed : null,
    })
  }

  return (
    <>
      <HubUrlSelectFilter
        label="State"
        filterKey="state"
        options={stateSelectOptions}
        value={filters.state}
        onChange={(value) => onFiltersChange({ state: value })}
        placeholder="Select state"
      />
      <HubUrlSelectFilter
        label="Market"
        filterKey="market"
        options={marketSelectOptions}
        value={filters.market}
        onChange={(value) => onFiltersChange({ market: value })}
        placeholder="Select market"
      />
      <AttributesSelector
        label="Worker ID"
        rootGroupTitle="Filter by"
        selectedAttributes={workerIdSelectedAttributes}
        onClearAll={() => {
          setWorkerIdInput('')
          onFiltersChange({ workerId: null })
        }}
        groups={
          <AttributesSelector.Group
            key="workerId"
            title="Worker ID"
            numberOfSelectedAttributes={workerIdSelectedAttributes.length}
          >
            <Box>
              <Input
                type="number"
                placeholder="Enter Worker ID"
                value={workerIdInput}
                onChange={(e) => setWorkerIdInput(e.target.value)}
                onBlur={applyWorkerId}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    applyWorkerId()
                  }
                }}
              />
            </Box>
          </AttributesSelector.Group>
        }
      />
      <AttributesSelector
        label="Worker name"
        rootGroupTitle="Filter by"
        selectedAttributes={workerNameSelectedAttributes}
        onClearAll={() => {
          setWorkerSearchInput('')
          onFiltersChange({ workerSearch: '' })
        }}
        groups={
          <AttributesSelector.Group
            key="workerName"
            title="Worker name"
            numberOfSelectedAttributes={workerNameSelectedAttributes.length}
          >
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Worker name
              </Text>
              <Input
                placeholder="Search worker name"
                value={workerSearchInput}
                onChange={(e) => setWorkerSearchInput(e.target.value)}
              />
            </Box>
          </AttributesSelector.Group>
        }
      />
    </>
  )
}
